import React, { useState, useEffect, useCallback } from "react";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { formatQuarterLabel } from "../../utils/utils";
import CoachingTabs from "./CoachingTabs";
import CoachingSkeleton from "../../components/layout/CoachingSkeleton";
import {
    MessageSquare, Star, Filter, TrendingUp
} from "lucide-react";

export default function CoachingFeedback() {
    const { isDarkMode } = useTheme();
    const [feedback, setFeedback] = useState([]);
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState("");
    const [quarterlyStats, setQuarterlyStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchFeedback = useCallback(async () => {
        try { const res = await api.get("/coach/my-feedback"); setFeedback(res.data.feedback || []); } catch { setFeedback([]); }
    }, []);
    const fetchQuarters = useCallback(async () => {
        try { const res = await api.get("/coach/quarters"); setQuarters(res.data.quarters || []); } catch { setQuarters([]); }
    }, []);
    const fetchQuarterlyStats = useCallback(async (qid) => {
        if (!qid) { setQuarterlyStats(null); return; }
        try { const res = await api.get(`/coach/quarterly-stats?quarter_id=${qid}`); setQuarterlyStats(res.data); } catch { setQuarterlyStats(null); }
    }, []);
    const fetchQuarterlyFeedback = useCallback(async (qid) => {
        if (!qid) { fetchFeedback(); return; }
        try { const res = await api.get(`/coach/feedback/quarterly?quarter_id=${qid}`); setFeedback(res.data.feedback || []); } catch { setFeedback([]); }
    }, [fetchFeedback]);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await Promise.all([fetchFeedback(), fetchQuarters()]);
            setLoading(false);
        };
        load();
    }, [fetchFeedback, fetchQuarters]);

    useEffect(() => {
        if (selectedQuarter) {
            fetchQuarterlyStats(selectedQuarter);
            fetchQuarterlyFeedback(selectedQuarter);
        } else {
            setQuarterlyStats(null);
            fetchFeedback();
        }
    }, [selectedQuarter, fetchQuarterlyStats, fetchQuarterlyFeedback, fetchFeedback]);

    if (loading) {
        return <CoachingSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
                <header className="mb-6">
                    <h1 className={`text-2xl md:text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                        Feedback
                    </h1>
                    <p className={isDarkMode ? "text-slate-400" : "text-slate-600"}>
                        Feedback and encouragement from your coach
                    </p>
                </header>

                <CoachingTabs className="md:hidden mb-6" />

                {/* Quarter Filter */}
                <div className={`flex items-center gap-3 mb-5 p-3 rounded-xl ${isDarkMode ? "bg-slate-800/70 border border-slate-700/60" : "bg-white border border-slate-200"}`}>
                    <Filter size={16} className={isDarkMode ? "text-slate-400" : "text-slate-500"} />
                    <label htmlFor="fb-quarter-filter" className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>Quarter:</label>
                    <select
                        id="fb-quarter-filter"
                        value={selectedQuarter}
                        onChange={(e) => setSelectedQuarter(e.target.value)}
                        className={`select-styled text-sm rounded-xl px-3 py-2 border font-medium shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
                    >
                        <option value="">All Time</option>
                        {quarters.map((q) => (
                            <option key={q.QuarterID} value={q.QuarterID}>{formatQuarterLabel(q)}</option>
                        ))}
                    </select>
                    {selectedQuarter && quarterlyStats && (
                        <div className="flex items-center gap-3 ml-auto text-xs">
                            <span className={isDarkMode ? "text-primary-400" : "text-primary-600"}>
                                <TrendingUp size={13} className="inline mr-1" />{quarterlyStats.completionRate || 0}% complete
                            </span>
                            <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
                                {quarterlyStats.feedbackCount || 0} feedback
                            </span>
                        </div>
                    )}
                </div>

                {feedback.length === 0 ? (
                    <div className={`rounded-2xl border p-12 text-center ${isDarkMode ? "bg-slate-800/60 border-slate-700/60" : "bg-white border-slate-200"}`}>
                        <div className={`w-20 h-20 mx-auto mb-5 rounded-2xl flex items-center justify-center ${isDarkMode ? "bg-slate-700/60" : "bg-slate-100"}`}>
                            <MessageSquare className={isDarkMode ? "text-slate-500" : "text-slate-300"} size={36} />
                        </div>
                        <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                            No feedback yet
                        </h3>
                        <p className={`text-sm max-w-sm mx-auto ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            Your coach's feedback and encouragement will appear here once they share their thoughts.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {feedback.map((f) => (
                            <div
                                key={f.FeedbackID}
                                className={`rounded-2xl border p-5 transition-all hover:shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                                        {f.CoachFirstName?.[0]}{f.CoachLastName?.[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-3 mb-2">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`font-semibold text-sm ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                    {f.CoachFirstName} {f.CoachLastName}
                                                </span>
                                                {f.Rating && (
                                                    <div className="flex items-center gap-0.5">
                                                        {[1, 2, 3, 4, 5].map((s) => (
                                                            <Star key={s} size={13} className={s <= f.Rating ? "text-amber-400 fill-amber-400" : isDarkMode ? "text-slate-600" : "text-slate-300"} />
                                                        ))}
                                                    </div>
                                                )}
                                                {f.TaskTitle && (
                                                    <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${isDarkMode ? "bg-cyan-900/30 text-cyan-300" : "bg-cyan-50 text-cyan-700"}`}>
                                                        Re: {f.TaskTitle}
                                                    </span>
                                                )}
                                            </div>
                                            <span className={`text-xs flex-shrink-0 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{f.CreatedOn}</span>
                                        </div>
                                        <p className={`text-sm leading-relaxed ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{f.FeedbackText}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
